import { useMemo } from 'react';

import {
  ENGAGEMENT_API_BASE_URL,
  MAPBOX_ACCESS_TOKEN,
  MARKET_API_GRPC_URL,
  SITE_CONFIG_API_BASE_URL,
} from '@m/config/env';
import { USE_MAPBOX_FOR_SITE_SEARCH, QA_SITE_SURVEY_ADDRESS_ARRAY } from '@m/config/FeatureFlags';
import { Destination } from '@m/state/destinations/types';
import { SiteWithHoursAndRate } from '@m/types/api/Site';
import { parseDeepLinkDateTime, utcToVenueWallClock } from '@m/utils/dateTimeUtils';
import { apiFetchV2, nonApiResponseFetch } from '@m/utils/http';
import { normalizeParkingAvailability } from '@m/utils/normalizeQuotePrice';
import mapbox from '@mapbox/mapbox-sdk';
import geocoder from '@mapbox/mapbox-sdk/services/geocoding';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { useFeatureGate } from '@m/hooks/data/featureFlags';

import { dedupeSearchDestinations, SEARCH_GEOCODE_TYPES } from './dedupeSearchDestinations';
import { formatMapboxPlaceLocality } from './mapboxPlaceLocality';

import type { TransactionFeePlans } from '@m/types/api/Fees';
import type { LocationSuggestionsResponse } from '@m/types/api/LocationSuggestion';
import type {
  MarketParkingAvailabilityParams,
  MarketParkingAvailabilityResponse,
  MarketParkingProductQuote,
  MarketZoneParkingQuote,
} from '@m/types/api/MarketParkingAvailability';
import type { FlatRate } from '@m/types/api/Rate';
import type { RateSchedule } from '@m/types/api/RateSchedule';
import type {
  DiscoverZone,
  RatesBatchResponse,
  ZoneRateSite,
  ZonesDiscoverParams,
  ZonesDiscoverResponse,
} from '@m/types/api/SiteConfig';

interface SitesSearchParams {
  latitude: number;
  longitude: number;
  maxDistanceMiles: number;
}

interface SitesSearchResponse {
  sites: SiteWithHoursAndRate[];
}

const mapboxClient = mapbox({ accessToken: MAPBOX_ACCESS_TOKEN });
const geocoderService = geocoder(mapboxClient);

interface SearchDestinationsParams {
  /**
   * When true, collapse near-duplicate Mapbox features (discovery search).
   * Off by default so admin address/prospect autocompletes keep nearby distinct
   * addresses that share a center within ~110m.
   */
  dedupeNearDuplicates?: boolean;
  enabled?: boolean;
  limit?: number;
  proximity?: [number, number];
  query: string;
}

interface SearchDestinationsResponse {
  apiResponse: any;
  features: Destination[];
}

interface ReverseGeocodeParams {
  lat: number;
  lng: number;
}

const searchDestinationsHandler = async ({
  query,
  proximity,
  limit = 10,
  dedupeNearDuplicates = false,
}: SearchDestinationsParams): Promise<SearchDestinationsResponse> => {
  if (!USE_MAPBOX_FOR_SITE_SEARCH) {
    const matchingAddress = QA_SITE_SURVEY_ADDRESS_ARRAY.find((each) =>
      each.address.includes(query),
    );
    if (matchingAddress !== undefined) {
      return {
        features: [
          {
            center: [matchingAddress?.lng, matchingAddress?.lat],
            value: matchingAddress?.address,
            context: [
              {
                id: matchingAddress?.address,
                short_code: matchingAddress?.address,
                text: matchingAddress?.address,
              },
            ],
            id: matchingAddress?.address,
            place_name: matchingAddress?.address,
            place_type: ['address'],
            properties: { address: matchingAddress?.address },
            text: matchingAddress?.address,
          } as Destination,
        ],
        apiResponse: {
          hasNextPage: () => false,
          nextPage: () => false,
          destinationsNext: () => false,
        },
      };
    }
    return {
      apiResponse: {
        hasNextPage: () => false,
        nextPage: () => false,
        destinationsNext: () => false,
      },
      features: QA_SITE_SURVEY_ADDRESS_ARRAY.map((each) => ({
        center: [each.lng, each.lat],
        value: each.address,
        context: [
          {
            id: each.address,
            short_code: each.address,
            text: each.address,
          },
        ],
        id: each.address,
        place_name: each.address,
        place_type: ['address'],
        properties: { address: each.address },
        text: each.address,
      })),
    };
  }

  const res = await geocoderService
    .forwardGeocode({
      query,
      mode: 'mapbox.places',
      proximity,
      countries: ['US'],
      // Discovery-only: keep admin prospect/address autocomplete on Mapbox defaults
      // (district/postcode/region/country). Opt in with dedupeNearDuplicates.
      ...(dedupeNearDuplicates ? { types: [...SEARCH_GEOCODE_TYPES] } : {}),
      limit,
    })
    .send();

  const { body } = res;
  const features = body.features.map((feature: any) => ({
    id: feature.id,
    center: feature.center as [number, number],
    text: feature.text,
    place_name: feature.place_name,
    place_type: feature.place_type,
    properties: feature.properties,
    context: feature.context || [],
    locality: formatMapboxPlaceLocality(feature.context, feature.text),
  }));
  return {
    features: dedupeNearDuplicates ? dedupeSearchDestinations(features) : features,
    apiResponse: res,
  };
};

const reverseGeocodeHandler = async ({
  lat,
  lng,
}: ReverseGeocodeParams): Promise<string | null> => {
  const res = await geocoderService
    .reverseGeocode({
      query: [lng, lat],
      mode: 'mapbox.places',
      countries: ['US'],
      limit: 1,
    })
    .send();

  return res.body.features?.[0]?.place_name ?? null;
};

export const useMapSitesSearch = (
  { latitude, longitude, maxDistanceMiles }: SitesSearchParams,
  options?: Partial<UseQueryOptions<SitesSearchResponse>>,
) =>
  useQuery({
    queryKey: ['customer-sites-search', latitude, longitude, maxDistanceMiles],
    queryFn: async () =>
      apiFetchV2<SitesSearchResponse>(`/customer/sites/search`, {
        params: {
          latitude,
          longitude,
          maxDistanceMiles,
        },
      }),
    enabled: !!(latitude && longitude && maxDistanceMiles),
    ...options,
  });

export const useSearchDestinations = (params: SearchDestinationsParams) =>
  useQuery({
    queryKey: [
      'search-destinations',
      params.query,
      params.proximity,
      params.limit,
      params.dedupeNearDuplicates ?? false,
    ],
    queryFn: () => searchDestinationsHandler(params),
    enabled: !!params.query && (params.enabled ?? true),
  });

const LOCATION_SUGGEST_TIMEOUT_MS = 3000;

export const fetchLocationSuggestions = async (
  query: string,
  _coordinates: { latitude: number; longitude: number } | null,
  signal?: AbortSignal,
): Promise<LocationSuggestionsResponse> => {
  const timeoutController = new AbortController();
  const timeoutId = window.setTimeout(() => timeoutController.abort(), LOCATION_SUGGEST_TIMEOUT_MS);
  const abortFromQuery = () => timeoutController.abort();
  signal?.addEventListener('abort', abortFromQuery, { once: true });

  try {
    return await nonApiResponseFetch<LocationSuggestionsResponse>('/api/v1/search/suggestions', {
      baseUrl: ENGAGEMENT_API_BASE_URL,
      method: 'GET',
      skipAuth: true,
      signal: timeoutController.signal,
      // Staging CloudFront only allowlists this path + `query`. Extra params
      // (including lat/lng) currently 401 the request.
      params: {
        query,
      },
    });
  } finally {
    window.clearTimeout(timeoutId);
    signal?.removeEventListener('abort', abortFromQuery);
  }
};

export const useLocationSuggestions = (
  query: string,
  coordinates: { latitude: number; longitude: number } | null,
  enabled: boolean,
) =>
  useQuery({
    // CloudFront only allowlists `query`, so lat/lng never reach the API —
    // keep them off the key or a late geolocation fix refetches the same list.
    queryKey: ['location-suggestions', query],
    queryFn: ({ signal }) => fetchLocationSuggestions(query, coordinates, signal),
    enabled: enabled && !!query,
    retry: false,
    staleTime: 30_000,
  });

export const useReverseGeocode = (coords: ReverseGeocodeParams | null) =>
  useQuery({
    queryKey: ['reverse-geocode', coords?.lat, coords?.lng],
    queryFn: () => (coords ? reverseGeocodeHandler(coords) : Promise.resolve(null)),
    enabled: !!coords,
  });

// ---------------------------------------------------------------------------
// Shared query behavior
// ---------------------------------------------------------------------------

// These discovery endpoints are read-mostly: suppress focus/mount refetches and
// only refresh on reconnect. Spread into each useQuery below.
const STATIC_QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: true,
  refetchOnMount: false,
} as const;

// Zone/rate data changes rarely — cache it aggressively (30m fresh / 60m kept).
const LONG_LIVED_CACHE = { staleTime: 1000 * 60 * 30, gcTime: 1000 * 60 * 60 } as const;

// Availability quotes are window-specific and time-sensitive (5m fresh / 15m kept).
const SHORT_LIVED_CACHE = { staleTime: 1000 * 60 * 5, gcTime: 1000 * 60 * 15 } as const;

// ---------------------------------------------------------------------------
// Zone Discovery + Rates Batch APIs
// ---------------------------------------------------------------------------

// The discover endpoint lives on site-config (gRPC-transcoded, raw proto JSON — no { success, data } wrapper)
const fetchZonesDiscover = (params: ZonesDiscoverParams): Promise<ZonesDiscoverResponse> =>
  nonApiResponseFetch<ZonesDiscoverResponse>('/api/v1/zones/discover', {
    baseUrl: SITE_CONFIG_API_BASE_URL,
    skipAuth: true,
    params: {
      'circle.center.latitude': params.latitude,
      'circle.center.longitude': params.longitude,
      'circle.radius': params.radiusMiles,
      'circle.unit': 'MILES',
    },
  });

const fetchRatesBatch = (zoneUuids: string[]): Promise<RatesBatchResponse> =>
  apiFetchV2<RatesBatchResponse>('/v1/rates/batch', {
    method: 'POST',
    body: { zone_uuids: zoneUuids },
  });

/** Proto JSON may send amenity/payment enums as names or ints — normalize to string tags. */
const DISCOVER_AMENITY_BY_NUMBER: Record<number, string> = {
  1: 'AMENITY_EV_CHARGER',
  2: 'AMENITY_WHEELCHAIR_ACCESSIBLE',
  3: 'AMENITY_PUBLIC_RESTROOM',
  4: 'AMENITY_IN_OUT_ACCESS',
  5: 'AMENITY_ON_SITE_STAFF',
  6: 'AMENITY_BATTERY_JUMP',
  7: 'AMENITY_TIRE_INFLATION',
};

const DISCOVER_PAYMENT_BY_NUMBER: Record<number, string> = {
  1: 'ACCEPTED_PAYMENT_VISA',
  2: 'ACCEPTED_PAYMENT_MASTERCARD',
  3: 'ACCEPTED_PAYMENT_AMEX',
  4: 'ACCEPTED_PAYMENT_DISCOVER',
};

function normalizeDiscoverTags(
  values: (string | number)[] | undefined,
  byNumber: Record<number, string>,
): string[] {
  if (!values?.length) return [];
  const tags: string[] = [];
  for (const value of values) {
    if (typeof value === 'string') {
      if (value && !/UNSPECIFIED$/i.test(value)) tags.push(value);
      continue;
    }
    const mapped = byNumber[value];
    if (mapped) tags.push(mapped);
  }
  return tags;
}

function normalizeImageUrls(urls: readonly string[] | undefined): string[] {
  return (urls ?? []).map((url) => url.trim()).filter((url) => url !== '');
}

function mergeZonesAndRates(
  zones: DiscoverZone[],
  rates: Record<string, ZoneRateSite>,
): SiteWithHoursAndRate[] {
  const result: SiteWithHoursAndRate[] = [];
  for (const zone of zones) {
    const rateSite = rates[zone.zoneUuid];
    // Zones without rate data are excluded — the rates batch is the source of display info.
    // This can happen when the rates API returns no entry for a given zone UUID.
    if (!rateSite) continue;
    result.push({
      id: rateSite.id ?? Number(zone.id),
      uuid: zone.zoneUuid,
      name: rateSite.name,
      lat: rateSite.lat,
      lng: rateSite.lng,
      latitude: rateSite.lat,
      longitude: rateSite.lng,
      streetAddress: rateSite.streetAddress,
      city: rateSite.city,
      stateCode: rateSite.stateCode,
      zipcode: rateSite.zipcode,
      timeZone: rateSite.timeZone,
      hoursOfOperation: rateSite.hoursOfOperation,
      allHoursOfOperation: [],
      rate: rateSite.rate,
      currentDayRateSchedule: rateSite.currentDayRateSchedule,
      rateSchedule: rateSite.rateSchedule,
      siteFees: rateSite.siteFees,
      // Zone-level reservation metadata from /zones/discover (same fields as availability).
      amenities: normalizeDiscoverTags(zone.amenities, DISCOVER_AMENITY_BY_NUMBER),
      acceptedPayments: normalizeDiscoverTags(zone.acceptedPayments, DISCOVER_PAYMENT_BY_NUMBER),
      // Proto3 JSON omits false booleans, so preserve the schema's false default.
      firstPartyReservationsEnabled: zone.firstPartyReservationsEnabled ?? false,
      imageUrls: normalizeImageUrls(zone.imageUrls),
    });
  }
  return result;
}

export const useZonesDiscover = (
  params: ZonesDiscoverParams | null,
  options?: Partial<UseQueryOptions<ZonesDiscoverResponse>>,
) =>
  useQuery({
    queryKey: ['zones-discover', params?.latitude, params?.longitude, params?.radiusMiles],
    queryFn: () => fetchZonesDiscover(params!),
    enabled: params != null && params.radiusMiles > 0,
    ...LONG_LIVED_CACHE,
    ...STATIC_QUERY_OPTIONS,
    ...options,
  });

export const useRatesBatch = (
  zoneUuids: string[] | null,
  options?: Partial<UseQueryOptions<RatesBatchResponse>>,
) =>
  useQuery({
    queryKey: ['rates-batch', zoneUuids ? [...zoneUuids].sort().join(',') : null],
    queryFn: () => fetchRatesBatch(zoneUuids!),
    enabled: !!(zoneUuids && zoneUuids.length > 0),
    ...LONG_LIVED_CACHE,
    ...STATIC_QUERY_OPTIONS,
    ...options,
  });

export function useDiscoverySites(params: ZonesDiscoverParams | null): {
  isError: boolean;
  isLoading: boolean;
  sites: SiteWithHoursAndRate[];
} {
  const discoverQuery = useZonesDiscover(params);

  const zoneUuids = useMemo(
    () => discoverQuery.data?.zones.map((z) => z.zoneUuid) ?? null,
    [discoverQuery.data],
  );

  // Empty discover results skip rates — settle as success once discover is done.
  const ratesQuery = useRatesBatch(zoneUuids);

  const sites = useMemo(
    () =>
      discoverQuery.data && ratesQuery.data
        ? mergeZonesAndRates(discoverQuery.data.zones, ratesQuery.data.rates)
        : [],
    [discoverQuery.data, ratesQuery.data],
  );

  const discoverFailed = discoverQuery.isError;
  const ratesFailed = !!zoneUuids?.length && ratesQuery.isError;

  return {
    sites,
    isLoading: discoverQuery.isLoading || ratesQuery.isLoading,
    isError: params != null && (discoverFailed || ratesFailed),
  };
}

// ---------------------------------------------------------------------------
// Market Parking Availability API (market-api — /api/v1/parking/availability)
//
// Powers the customer Discovery Map. Unlike the zones/discover + rates/batch
// pair above, this is a single public, gRPC-transcoded GET that returns zones
// with parking product quotes (total trip price for the requested window).
// ---------------------------------------------------------------------------

export const fetchParkingAvailability = (
  params: MarketParkingAvailabilityParams,
): Promise<MarketParkingAvailabilityResponse> =>
  nonApiResponseFetch<MarketParkingAvailabilityResponse>('/api/v1/parking/availability', {
    baseUrl: MARKET_API_GRPC_URL,
    skipAuth: true,
    params: {
      latitude: params.latitude,
      longitude: params.longitude,
      radius: params.radiusMiles,
      arrival_datetime: params.arrivalDatetime,
      return_datetime: params.returnDatetime,
      ...(params.terminal ? { terminal: params.terminal } : {}),
      ...(params.zoneId ? { zone_id: params.zoneId } : {}),
      ...(params.eventId ? { event_id: params.eventId } : {}),
      ...(params.venueId ? { venue_id: params.venueId } : {}),
    },
  }).then(normalizeParkingAvailability);

export const useMarketParkingAvailability = (
  params: MarketParkingAvailabilityParams | null,
  options?: Partial<UseQueryOptions<MarketParkingAvailabilityResponse>>,
) => {
  // The first-party reservations backend integration is gated by the
  // `web_first-party-reservations-enabled` Statsig flag. Gating lives here, at
  // the integration source, so the availability request can never fire when the
  // flag is off — no matter which caller invokes the hook.
  const { data: firstPartyReservationsGate } = useFeatureGate(
    'web_first-party-reservations-enabled',
  );
  const firstPartyReservationsEnabled = firstPartyReservationsGate?.enabled ?? false;
  const { data: freeTextSearchGate } = useFeatureGate('growth_free-text-search-enabled');
  // Only strip scope IDs once the gate has resolved off. Pending must not look
  // like `enabled: false` or the first request drops event/venue/zone filters.
  const freeTextSearchDisabled = freeTextSearchGate?.enabled === false;
  let requestParams = params;
  if (params && freeTextSearchDisabled) {
    requestParams = { ...params, eventId: undefined, venueId: undefined, zoneId: undefined };
  }

  return useQuery({
    queryKey: [
      'market-parking-availability',
      params?.latitude,
      params?.longitude,
      params?.radiusMiles,
      params?.arrivalDatetime,
      params?.returnDatetime,
      params?.terminal,
      freeTextSearchDisabled ? undefined : params?.zoneId,
      freeTextSearchDisabled ? undefined : params?.eventId,
      freeTextSearchDisabled ? undefined : params?.venueId,
    ],
    queryFn: () => fetchParkingAvailability(requestParams!),
    ...SHORT_LIVED_CACHE,
    ...STATIC_QUERY_OPTIONS,
    ...options,
    // Keep `enabled` last so the gate can't be overridden by a caller's
    // `options.enabled`. A caller may still further disable the query (the
    // `?? true` honors an explicit `enabled: false`), but can never enable the
    // request past the Statsig gate.
    enabled:
      firstPartyReservationsEnabled &&
      params != null &&
      params.radiusMiles > 0 &&
      (options?.enabled ?? true),
  });
};

// Stable non-negative integer id derived from the zone UUID. The discovery UI
// keys/dedupes/selects sites by numeric id (React keys, marker selection), but
// availability zones only carry a UUID — so we hash it to a number. A simple
// polynomial hash kept under 2^40 (well within JS's safe-integer range, so plain
// arithmetic suffices — no bitwise ops) makes collisions between two distinct
// zones vanishingly unlikely for any realistic result set, though not impossible.
const ZONE_ID_MODULUS = 2 ** 40;
function hashUuidToId(uuid: string): number {
  let hash = 0;
  for (let i = 0; i < uuid.length; i += 1) {
    hash = (hash * 31 + uuid.charCodeAt(i)) % ZONE_ID_MODULUS;
  }
  return hash;
}

function getValidQuotePriceValue(quote: MarketParkingProductQuote): number | undefined {
  if (quote.price == null) return undefined;
  const value = quote.price.value ?? 0;
  return value >= 0 ? value : undefined;
}

export function getCheapestQuote(
  quotes: MarketParkingProductQuote[],
): MarketParkingProductQuote | undefined {
  return quotes.reduce<MarketParkingProductQuote | undefined>((cheapest, quote) => {
    const value = getValidQuotePriceValue(quote);
    if (value == null || quote.price == null) return cheapest;
    const normalizedQuote =
      quote.price.value == null ? { ...quote, price: { ...quote.price, value } } : quote;
    if (!cheapest) return normalizedQuote;
    return value < (cheapest.price?.value ?? 0) ? normalizedQuote : cheapest;
  }, undefined);
}

// Synthesize a FlatRate so the existing discovery rate formatters render the
// quote's total trip price over the requested window (e.g. "$45.00 up to 2 hours").
function buildFlatRateFromPrice(price: number, windowMs: number): FlatRate {
  return {
    freePeriod: 0,
    id: 0,
    maxCost: null,
    maxPeriod: windowMs,
    maxPrice: price,
    minCost: null,
    price,
    pricePerPeriod: price,
    priceWithFee: price,
    rateUuid: '',
    type: { id: 2, name: 'flatRate' },
  };
}

const EMPTY_RATE_SCHEDULE: RateSchedule = { rateSpanSchedules: [], siteId: 0, siteUuid: '' };
const EMPTY_SITE_FEES: TransactionFeePlans = [];

/**
 * Availability `bufferStart`/`bufferEnd` are UTC instants. Convert them to the
 * event venue's wall-clock (literal `Z`) so they match the rest of the event flow.
 * Without a `timeZone` (or if it is invalid) the value is returned unchanged.
 */
export function bufferToVenueWallClock(value?: string, timeZone?: string): string | undefined {
  if (!value || !timeZone) return value;
  return utcToVenueWallClock(value, timeZone) ?? value;
}

function siteBufferWindowMs(
  zone: MarketZoneParkingQuote,
  eventTimeZone?: string,
): number | undefined {
  const bufferStart = bufferToVenueWallClock(zone.zoneDetails?.bufferStart, eventTimeZone);
  const bufferEnd = bufferToVenueWallClock(zone.zoneDetails?.bufferEnd, eventTimeZone);
  if (!bufferStart || !bufferEnd) return undefined;
  const start = parseDeepLinkDateTime(bufferStart);
  const end = parseDeepLinkDateTime(bufferEnd);
  if (!start || !end) return undefined;
  const ms = end.getTime() - start.getTime();
  return Number.isFinite(ms) && ms > 0 ? ms : undefined;
}

function mapAvailabilityZonesToSites(
  zones: MarketZoneParkingQuote[],
  windowMs: number,
  eventTimeZone?: string,
): SiteWithHoursAndRate[] {
  const sites: SiteWithHoursAndRate[] = [];
  for (const zone of zones) {
    const cheapest = getCheapestQuote(zone.quotes ?? []);
    // A zone with no valid priced quote isn't bookable for the requested window.
    // Zero is valid for fully discounted/free products; only missing or negative
    // prices are excluded before selecting the cheapest quote.
    if (cheapest?.price == null) continue;
    // Union of feature tags across all priced quotes for the zone — used by the
    // discovery filters. De-duplicated; empty when no quote carries features.
    const features = Array.from(
      new Set(
        (zone.quotes ?? [])
          .filter((quote) => getValidQuotePriceValue(quote) != null)
          .flatMap((quote) => quote.features ?? []),
      ),
    );
    // Event-rate lots: prefer buffer duration so "up to X hours" matches the
    // Event rate tag / checkout parking window when those differ from search.
    const rateWindowMs = siteBufferWindowMs(zone, eventTimeZone) ?? windowMs;
    sites.push({
      id: hashUuidToId(zone.zoneUuid),
      uuid: zone.zoneUuid,
      name: zone.zoneName,
      lat: zone.latitude,
      lng: zone.longitude,
      latitude: zone.latitude,
      longitude: zone.longitude,
      // Address/hours come from rates/batch enrichment — availability omits them.
      hoursOfOperation: null,
      allHoursOfOperation: [],
      rate: buildFlatRateFromPrice(cheapest.price.value, rateWindowMs),
      currentDayRateSchedule: EMPTY_RATE_SCHEDULE,
      rateSchedule: EMPTY_RATE_SCHEDULE,
      siteFees: EMPTY_SITE_FEES,
      features,
      isEventRate: cheapest.price.tariffType === 'EVENT',
      bufferStart: bufferToVenueWallClock(zone.zoneDetails?.bufferStart, eventTimeZone),
      bufferEnd: bufferToVenueWallClock(zone.zoneDetails?.bufferEnd, eventTimeZone),
      amenities: zone.zoneDetails?.amenities ?? [],
      acceptedPayments: zone.zoneDetails?.acceptedPayments ?? [],
      firstPartyReservationsEnabled: zone.zoneDetails?.firstPartyReservationsEnabled ?? true,
    });
  }
  return sites;
}

/**
 * Overlay discover + rates/batch fields onto availability-mapped sites.
 *
 * Availability quotes remain the list source of truth (`rate` = trip total).
 * Discover/rates fill what parking-availability omits: address, hours,
 * amenities, accepted payments, and drive-up rate schedules.
 */
export function enrichAvailabilitySitesWithDiscover(
  sites: SiteWithHoursAndRate[],
  rates: Record<string, ZoneRateSite> | undefined,
  discoverZones: DiscoverZone[] | undefined,
): SiteWithHoursAndRate[] {
  if (!rates && !discoverZones?.length) return sites;

  const discoverByUuid = new Map(
    (discoverZones ?? []).map((zone) => [zone.zoneUuid, zone] as const),
  );

  return sites.map((site) => {
    if (!site.uuid) return site;
    const rateSite = rates?.[site.uuid];
    const discoverZone = discoverByUuid.get(site.uuid);
    if (!rateSite && !discoverZone) return site;

    const discoverAmenities = normalizeDiscoverTags(
      discoverZone?.amenities,
      DISCOVER_AMENITY_BY_NUMBER,
    );
    const discoverPayments = normalizeDiscoverTags(
      discoverZone?.acceptedPayments,
      DISCOVER_PAYMENT_BY_NUMBER,
    );
    const discoverImageUrls = normalizeImageUrls(discoverZone?.imageUrls);

    return {
      ...site,
      // Prefer rates-batch numeric id when present (stable with discover mode).
      id: rateSite?.id ?? site.id,
      streetAddress: rateSite?.streetAddress ?? site.streetAddress,
      city: rateSite?.city ?? site.city,
      stateCode: rateSite?.stateCode ?? site.stateCode,
      zipcode: rateSite?.zipcode ?? site.zipcode,
      timeZone: rateSite?.timeZone ?? site.timeZone,
      hoursOfOperation: rateSite?.hoursOfOperation ?? site.hoursOfOperation,
      currentDayRateSchedule: rateSite?.currentDayRateSchedule ?? site.currentDayRateSchedule,
      rateSchedule: rateSite?.rateSchedule ?? site.rateSchedule,
      siteFees: rateSite?.siteFees?.length ? rateSite.siteFees : site.siteFees,
      // Drive-up rate for details; keep reservation trip quote on `rate`.
      driveUpRate: rateSite?.rate ?? site.driveUpRate,
      // Prefer discover tags when present; fall back to availability zoneDetails.
      amenities: discoverAmenities.length ? discoverAmenities : site.amenities,
      acceptedPayments: discoverPayments.length ? discoverPayments : site.acceptedPayments,
      firstPartyReservationsEnabled:
        discoverZone?.firstPartyReservationsEnabled ?? site.firstPartyReservationsEnabled,
      imageUrls: discoverImageUrls.length ? discoverImageUrls : site.imageUrls,
    };
  });
}

/**
 * Discovery sites backed by the market parking-availability API. Drop-in
 * replacement for {@link useDiscoverySites} on the customer Discovery Map.
 *
 * Also fetches zones/discover + rates/batch for the same viewport so list/detail
 * can show hours, address, amenities, and drive-up rates alongside trip quotes.
 */
export function useDiscoveryAvailabilitySites(params: MarketParkingAvailabilityParams | null): {
  isError: boolean;
  isLoading: boolean;
  sites: SiteWithHoursAndRate[];
} {
  const query = useMarketParkingAvailability(params);

  // Depend on the window primitives (not the params object, which is rebuilt
  // each render) so the mapping only recomputes when data or the window changes.
  // When params is null (date window cleared), never surface cached market quotes.
  const arrivalDatetime = params?.arrivalDatetime;
  const returnDatetime = params?.returnDatetime;
  const eventTimeZone = params?.eventTimeZone;
  const zones = params ? query.data?.zones : undefined;

  const zoneUuids = useMemo(() => {
    if (!zones?.length) return null;
    return zones.map((zone) => zone.zoneUuid);
  }, [zones]);

  // Same discover + rates sources as browse mode — availability omits these fields.
  const discoverQuery = useZonesDiscover(
    params
      ? {
          latitude: params.latitude,
          longitude: params.longitude,
          radiusMiles: params.radiusMiles,
        }
      : null,
  );
  const ratesQuery = useRatesBatch(zoneUuids);

  const sites = useMemo(() => {
    if (!zones || arrivalDatetime == null || returnDatetime == null) return [];
    const diffMs = Date.parse(returnDatetime) - Date.parse(arrivalDatetime);
    const windowMs = Number.isFinite(diffMs) ? Math.max(0, diffMs) : 0;
    return enrichAvailabilitySitesWithDiscover(
      mapAvailabilityZonesToSites(zones, windowMs, eventTimeZone),
      ratesQuery.data?.rates,
      discoverQuery.data?.zones,
    );
  }, [
    zones,
    arrivalDatetime,
    returnDatetime,
    eventTimeZone,
    ratesQuery.data?.rates,
    discoverQuery.data?.zones,
  ]);

  // Don't block the map on discover/rates enrichment — quotes can render first.
  // Coverage / retry only care whether the availability request itself failed.
  return {
    sites,
    isLoading: params != null && query.isLoading,
    isError: params != null && query.isError,
  };
}
